<script>
import EditableText from '@components/editables/editable-text.vue';
import PhotoUploader from '@components/uploaders/photo-uploader.vue';
import { prospectModelMixin } from '@mixins/models/prospect-model-mixin';
import formatPhoneNumber from '@utils/format-phone-number';
import isPhone from '@utils/phone-validation';
import phoneMask from '@utils/phone-mask';
import { mask } from 'vue-the-mask';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { required } from 'vuelidate/lib/validators';
import {
  adminPanelComputed,
  adminPanelMethods,
  authComputed,
  mediaComputed,
  mediaMethods,
  modalComputed,
  modalMethods,
  prospectComputed,
  prospectMethods,
} from '@state/helpers';

const touchMap = new WeakMap();

export default {
  mixins: [prospectModelMixin],
  components: {
    EditableText,
    PhotoUploader,
  },
  directives: { mask },
  props: {
    prospect: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  validations: {
    prospect: {
      firstName: {
        required,
        isPlaceholder(value) {
          if (value === 'First Name') {
            return false;
          } else return true;
        },
      },
      lastName: {
        required,
        isPlaceholder(value) {
          if (value === 'Last Name') {
            return false;
          } else return true;
        },
      },
      phoneConnOpts: {
        $each: {
          isPhone,
        },
      },
      emailConnOpts: {
        $each: {
          required,
          validateEmail(obj) {
            if (obj) {
              return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
                obj.value
              );
            } else return true;
          },
        },
      },
    },
  },
  data() {
    return {
      hasUnsavedChanges: false,
      imgPlaceHolder: require('@assets/images/placeholder-photo-upload.svg'),
      initProspect: null,
      isSelected: false,
      isSubmitting: false,
      listenToError: false,
      newEmails: [],
      newEmailObj: {
        type: 'email',
        isPreferred: false,
        value: '',
      },
      newPhoneNumbers: [],
      newPhoneObj: {
        type: 'phone',
        isPreferred: false,
        value: '',
      },
      photoPreview: null,
      phoneMask: phoneMask,
    };
  },
  computed: {
    ...adminPanelComputed,
    ...authComputed,
    ...mediaComputed,
    ...modalComputed,
    ...prospectComputed,
    prospectImg() {
      return this.tempPhotoPreview
        ? this.tempPhotoPreview
        : this.selectedProspect.mediaAssets.avatarImage.url
        ? this.selectedProspect.mediaAssets.avatarImage.url
        : this.imgPlaceHolder;
    },
  },
  mounted() {
    this.initProspect = cloneDeep(this.selectedProspect);
    if (!this.selectedProspect?.phoneConnOpts?.length) {
      this.addPhoneNum();
    }

    if (!this.selectedProspect?.emailConnOpts?.length) {
      this.addEmail();
    }
  },
  methods: {
    ...adminPanelMethods,
    ...mediaMethods,
    ...modalMethods,
    ...prospectMethods,
    delayTouch($v) {
      $v.$reset();
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v));
      }
      touchMap.set($v, setTimeout($v.$touch, 1000));
    },
    onSelectPreferredConnOpt(type, opt, i) {
      if (!opt.isPreferred) {
        this.setTogglePreferredConnOpt({ type, opt, i: parseInt(i) });
      }
    },
    updateFirstName(value) {
      this.setSelectedProspectFirstName(value);
    },
    updateLastName(value) {
      this.setSelectedProspectLastName(value);
    },
    updatePhoneConnOpt(val, i) {
      this.setUpdateSelectedProspectPhoneConnOpt({ val, i });
    },
    updateEmailConnOpt(val, i) {
      this.setUpdateSelectedProspectEmailConnOpt({ val, i });
    },
    updateNewEmailConnOpt(val, i) {
      this.newEmails[i].value = val;
    },
    updateNewPhoneConnOpt(val, i) {
      this.newPhoneNumbers[i].value = val;
    },
    addEmail() {
      const newEmails = this.newEmails;
      const lastEmail = newEmails[newEmails.length - 1];

      if (!newEmails.length) {
        newEmails.push({ id: newEmails.length + 1, ...this.newEmailObj });
      } else if (lastEmail && lastEmail.email) {
        const isLastEmailValid =
          lastEmail.email.includes('@') && lastEmail.email.includes('.');

        if (isLastEmailValid) {
          newEmails.push({ id: newEmails.length + 1, ...this.newEmailObj });
        }
      }
    },
    addPhoneNum() {
      const newPhoneNumbers = this.newPhoneNumbers;
      const lastPhoneNum = newPhoneNumbers[newPhoneNumbers.length - 1];
      if (
        !newPhoneNumbers.length ||
        (lastPhoneNum &&
          lastPhoneNum.value.length >= 14 &&
          isPhone(lastPhoneNum.value))
      ) {
        newPhoneNumbers.push({
          id: newPhoneNumbers.length + 1,
          ...this.newPhoneObj,
        });
      }
    },
    cancelChanges() {
      this.setSelectedProspect(this.initProspect);
      this.setTempPhotoPreview(null);
      this.setIsNewMediaAsset(false);
      this.newEmails = [];
      this.newPhoneNumbers = [];
      this.listenToError = false;
      this.$v.$reset();
      this.hasUnsavedChanges = false;
    },
    handleProspectError(err) {
      const error = err.response.data;
      // Possibly handle other use cases. For now, handle connOpt errors:
      if (error.connectionOption) {
        const msg = error.message;
        const connOpt = error.connectionOption;
        this.setNotifyModalContext({
          textMain: `Oops!`,
          textSecond: `${msg} ${
            connOpt.type === 'phone'
              ? formatPhoneNumber(connOpt.value)
              : connOpt.value
          }`,
        });
        this.$bvModal.show('notify-modal');
      }
    },
    buildpayload() {
      let newConnOpts = this.newEmails
        .concat(this.newPhoneNumbers)
        .map((opt) => {
          delete opt.id;
          return opt;
        });
      // TODO: Currently no mediaAssests in Prospect Shape. Confirm with API devs.
      // const newMedia = this.hasNewMedia('cover', 'prospects');
      // if (newMedia) {
      //   this.setSelectedProspectAvatar(newMedia.publicUrl);
      // }
      newConnOpts = newConnOpts.filter((opt) => opt.value);
      this.setUpdateSelectedProspectConnOpts(newConnOpts);
    },
    async handleUpdate() {
      await this.updateProspect({ prospect: this.selectedProspect })
        .then((res) => {
          if (res && res.prospect) {
            this.isSubmitting = false;
            this.listenToError = false;
            this.initProspect = cloneDeep(this.selectedProspect);
            this.hasUnsavedChanges = false;
            this.setHasUnsavedProspectChanges(false);
            this.setIsNewMediaAsset(false);
            this.clearMedia();
            this.$v.$reset();
          }
        })
        .catch(async (err) => {
          await this.handleProspectError(err);
          this.isSubmitting = false;
          this.listenToError = false;
          this.$v.$reset();
        });
      this.hasUnsavedChanges = false;
    },
    async save() {
      this.isSubmitting = true;
      await this.buildpayload();
      this.handleUpdate();
    },
  },
  watch: {
    prospect: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal && oldVal) {
          this.hasUnsavedChanges = !isEqual(
            this.initProspect,
            this.selectedProspect
          );

          if (!this.hasUnsavedChanges) {
            this.setDiscardProspectChanges(false);
            this.$v.$reset();
          }
          this.setHasUnsavedProspectChanges(this.hasUnsavedChanges);
        }
      },
    },
    discardProspectChanges(newVal, oldVal) {
      if (newVal) {
        this.hasUnsavedChanges = false;
        this.setHasUnsavedProspectChanges(this.hasUnsavedChanges);
        this.setSelectedProspect(this.initProspect);
        this.$v.$reset();
      }
    },
    newEmails(newVal, oldVal) {
      if (this.selectedProspect?.emailConnOpts?.length && newVal.length) {
        this.hasUnsavedChanges = true;
      }
    },
    newPhoneNumbers: {
      deep: true,
      handler(newVal, oldVal) {
        if (
          (this.selectedProspect?.phoneConnOpts?.length && newVal.length) ||
          (newVal.length && newVal[0].value !== '')
        ) {
          this.hasUnsavedChanges = true;
        } else {
          this.hasUnsavedChanges = false;
        }
      },
    },
  },
};
</script>

<template>
  <form @submit.prevent="save">
    <div class="row">
      <div
        v-if="!prospect"
        class="col-2 prospect-profile-image"
        :style="{ 'background-image': `url(${prospectImg})` }"
      />
      <div
        class="col-5 prospect-name"
        :class="[
          {
            'input-error':
              $v.prospect.firstName.$error || $v.prospect.lastName.$error
                ? $v.prospect.firstName.$error || $v.prospect.lastName.$error
                : null,
          },
        ]"
      >
        <label>Name <sup class="required">required</sup></label>
        <div
          class="editable-div"
          :class="[{ 'input-error': $v.prospect.firstName.$error }]"
        >
          <h1 class="hero-title">
            <EditableText
              v-model.lazy="$v.prospect.firstName.$model"
              :text="prospect.firstName || prospect.firstName"
              placeholderTxt="First Name"
              mode="edit"
              :canEdit="true"
              @update-editable-value="updateFirstName"
            />
          </h1>
        </div>
        <div
          class="editable-div"
          :class="[{ 'input-error': $v.prospect.lastName.$error }]"
        >
          <h1 class="hero-title">
            <EditableText
              v-model.lazy="$v.prospect.lastName.$model"
              :text="prospect.lastName"
              placeholderTxt="Last Name"
              mode="edit"
              :canEdit="true"
              @update-editable-value="updateLastName"
            />
          </h1>
        </div>
        <transition name="fade">
          <span
            v-if="$v.prospect.firstName.$error || $v.prospect.lastName.$error"
            class="input-error-text"
          >
            {{
              $v.prospect.firstName.$error && $v.prospect.lastName.$error
                ? 'First name and Last name required'
                : $v.prospect.firstName.$error
                ? 'First name is required'
                : 'Last name is required'
            }}
          </span>
        </transition>
      </div>
    </div>
    <div class="row contact-info">
      <div class="col-4">
        <label>Email <sup class="required">required</sup></label>
        <BaseInputText
          name="email"
          v-for="(opt, i) in $v.prospect.emailConnOpts.$each.$iter"
          :key="i"
          :class="[{ 'input-error': opt.$invalid || opt.$error }]"
          :selectable="prospect.emailConnOpts.length > 1"
          :isSelected="opt.$model.isPreferred"
          :error="
            opt.$invalid || opt.$error ? 'A valid email is required' : null
          "
          :value="opt.$model.value"
          placeholder="Add prospect email"
          @handle-input-selection="
            onSelectPreferredConnOpt('email', opt.$model, i)
          "
          @input="updateEmailConnOpt($event.target.value, i)"
        />

        <div class="row">
          <div class="col">
            <transition-group name="fade">
              <div v-for="(input, i) in newEmails" :key="i">
                <BaseInputText
                  v-model="input.value"
                  placeholder="Add prospect email"
                  @input="updateNewEmailConnOpt($event.target.value, i)"
                />
              </div>
            </transition-group>
            <a class="link" @click="addEmail">
              Add Another Email Address
            </a>
          </div>
        </div>
      </div>

      <div class="col-3">
        <label>Phone</label>
        <BaseInputText
          name="phone"
          v-mask="phoneMask(opt.$model.value)"
          v-for="(opt, i) in $v.prospect.phoneConnOpts.$each.$iter"
          :key="i"
          :class="[
            { 'input-error': opt.$model.value && (opt.$invalid || opt.$error) },
          ]"
          :selectable="prospect.phoneConnOpts.length > 1"
          :isSelected="opt.$model.isPreferred"
          :error="
            opt.$model.value && (opt.$invalid || opt.$error)
              ? 'A valid phone number is required'
              : null
          "
          :value="opt.$model.value"
          placeholder="+1 (555) 555-5555"
          @handle-input-selection="
            onSelectPreferredConnOpt('phone', opt.$model, i)
          "
          @input="updatePhoneConnOpt($event.target.value, i)"
        />

        <div class="row">
          <div class="col">
            <transition-group name="fade">
              <div v-for="(input, i) in newPhoneNumbers" :key="i">
                <BaseInputText
                  v-mask="phoneMask(input.value)"
                  v-model="input.value"
                  placeholder="+1 (555) 555-5555"
                  @input="updateNewPhoneConnOpt($event.target.value, i)"
                />
              </div>
            </transition-group>
            <a class="link" @click="addPhoneNum">
              Add Another Phone Number
            </a>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col profile-picture-upload">
        <label>Profile Picture</label>
        <PhotoUploader
          requestType="prospects"
          :hidePlaceholder="true"
          :hidePhotoPreview="true"
          type="cover"
          helper-text="Photos must be .jpg, .jpeg, .gif, or .webp, no larger than 10MB."
        />
      </div>
    </div>

    <transition name="slide-fade">
      <div
        v-if="hasUnsavedChanges || isNewMediaAsset"
        :class="[
          'panel-footer',
          {
            'input-error': listenToError && $v.$invalid,
            'new-prospect': !prospect,
          },
        ]"
        ><span v-if="listenToError && $v.$invalid" class="error-instruction"
          >Fix highlighted errors.</span
        >
        <span v-if="!$v.$invalid || !listenToError" class="not-saved"
          >Not saved</span
        >
        <BaseButton
          type="button"
          class="btn-primary btn-clear"
          @click="cancelChanges"
        >
          <BaseIcon name="times" />
          Cancel Changes
        </BaseButton>
        <span class="divider-vertical">&nbsp;</span>
        <BaseButton
          :class="[
            {
              'input-error':
                isSubmitting ||
                $v.$invalid ||
                (isNewMediaAsset && signedPostData.length === 0),
            },
          ]"
          type="button"
          class="btn-primary btn-clear"
          @click="save"
          :disabled="
            isSubmitting ||
              $v.$invalid ||
              (isNewMediaAsset && signedPostData.length === 0)
          "
        >
          <BaseIcon
            v-if="
              !isSubmitting &&
              ((isNewMediaAsset && signedPostData.length > 0) ||
                !isNewMediaAsset)
                ? true
                : false
            "
            name="save"
          />
          <BaseIcon
            v-if="
              (isNewMediaAsset && signedPostData.length === 0) || isSubmitting
            "
            name="sync"
            spin
          />
          Save Changes
        </BaseButton>
      </div>
    </transition>
  </form>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable no-descending-specificity
// stylelint-disable selector-pseudo-element-no-unknown
// stylelint-disable selector-max-type

.required {
  color: $color-red;
  font-size: 0.8rem;
  font-weight: bold;
  left: 0.5rem;
  top: 0.1rem;
}

.section-title {
  margin-top: 4rem;
  width: 100%;
}

.col-4 {
  .section-title {
    margin-right: 13rem;
  }
}

::v-deep .hero-title {
  margin-bottom: unset !important;

  .input-inline-edit {
    outline-style: none;

    &::placeholder {
      opacity: 0.5;
    }
  }
}

.prospect-name {
  margin-bottom: 4rem;

  label {
    margin-bottom: 0;
  }

  .input-error-text {
    color: transparent;
    opacity: 0;
    transition: opacity $duration-animation-base ease-in-out;
  }

  &.input-error {
    .input-error-text {
      color: $color-input-error-dark;
      display: block;
      margin-bottom: unset;
      margin-top: 1.2rem;
      opacity: 1;
    }
  }

  .editable-div {
    font-size: 3.8rem;
    font-weight: 900;
    line-height: 5.3rem;
    outline-style: none;
    transition: background-image $duration-animation-base ease-in-out;
    width: 100%;

    &.input-error {
      background-image: -webkit-linear-gradient(
        top,
        transparent,
        transparent 51px,
        rgba($color-red-wo-rgb, 0.945) 10px
      );
    }
  }
}

.contact-info {
  margin-bottom: 4rem;

  ::v-deep .input {
    margin-bottom: 1rem;
  }

  .link {
    text-decoration: none;
    text-transform: capitalize;
  }
}

.prospect-profile-image {
  align-self: center;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: $base-border-radius-md + 0.2;
  height: 12rem;
  margin-left: 2rem;
  margin-right: 2rem;
  max-width: 12.5rem;
  width: 2rem;
}

.profile-picture-upload {
  margin-bottom: 4.9rem;

  label {
    margin-bottom: unset;
  }

  ::v-deep {
    .photo-preview {
      margin-bottom: 0.5rem;
    }

    .input-photo {
      width: 18rem;
    }
  }
}

.panel-footer {
  background-color: black;
  border-radius: $size-input-border-radius;
  bottom: 10rem;
  display: flex;
  justify-content: flex-end;
  left: 2rem;
  margin-left: 19rem;
  padding: 1rem 2rem;
  position: fixed;
  right: 2rem;
  z-index: 9999;

  .error-instruction {
    align-self: center;
    color: white;
    font-family: $base-font-family;
    font-size: 1.4rem;
    font-style: normal;
    font-weight: 600;
    left: 2.2rem;
    position: absolute;
  }

  .not-saved {
    @extend .error-instruction;

    opacity: 0.5;
  }

  .input-error {
    opacity: 0.6;
  }

  &.new-prospect {
    background-color: $color-header-bg;
    margin-left: unset;

    &.input-error {
      background-color: $color-error-dark;
    }
  }

  &.input-error {
    background-color: $color-error-dark;
  }

  .divider-vertical {
    border-left: 1px solid white;
    height: 2.5rem;
  }

  .btn-primary {
    &:active {
      background: rgba($color-white-wo-rgb, 0.1);
      color: white;
    }

    &:focus {
      background: transparent;
      color: white;
    }

    &:not(:disabled):not(.disabled) {
      &:active {
        background: rgba($color-white-wo-rgb, 0.1);
        color: white;
      }

      &:focus {
        background: transparent;
        color: white;
      }
    }

    &:disabled {
      opacity: 0.5;
    }
  }

  .btn-clear {
    color: white;
  }
}

@media screen and (max-width: 868px) {
  .col-5 {
    max-width: 50%;
  }

  .col-3 {
    flex: 0 0 40%;
    max-width: 35%;
  }

  .col-6 {
    flex: 0 0 60%;
    margin-left: 4%;
    max-width: 58%;

    .col-5 {
      flex: 0 0 49%;
      max-width: 50%;
    }
  }

  .col-4 {
    flex: 0 0 60.333333%;
    max-width: 38%;
  }
}

// ANIMATIONS

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transform: translateY(0%);
  transition: all $duration-animation-base ease;
}

.slide-fade-leave-active {
  transform: translateY(0%);
  transition: all $duration-animation-base ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(100%);
}
</style>
